/**
 * Company Setup Edit Requests - Admin Review Interface
 * Beautiful admin dashboard for reviewing and approving/rejecting locked field edit requests
 * Features: Filtering, bulk actions, detailed review dialog, audit trail
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Field,
  Text,
  Spinner,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Textarea,
  Avatar,
  Tab,
  TabList,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ClockRegular,
  SearchRegular,
  InfoRegular,
  HistoryRegular,
  ArrowLeftRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  backButton: {
    minWidth: 'auto',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#323130',
    background: 'linear-gradient(135deg, #008272 0%, #00635a 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
    marginTop: '4px',
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  searchBox: {
    flex: '1 1 300px',
    minWidth: '250px',
  },
  tabContainer: {
    marginBottom: '24px',
  },
  primaryButton: {
    backgroundColor: '#008272',
    color: '#ffffff',
    fontWeight: '600',
    ':hover': {
      backgroundColor: '#00635a',
    },
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    color: '#008272',
    border: '1px solid #008272',
    fontWeight: '600',
    ':hover': {
      backgroundColor: 'rgba(0, 130, 114, 0.05)',
    },
  },
  approveButton: {
    backgroundColor: '#107c10',
    color: '#ffffff',
    ':hover': {
      backgroundColor: '#0b5f0b',
    },
  },
  rejectButton: {
    backgroundColor: '#d13438',
    color: '#ffffff',
    ':hover': {
      backgroundColor: '#a4262c',
    },
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  pendingBadge: {
    backgroundColor: '#fef3cd',
    color: '#92400e',
  },
  approvedBadge: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },
  rejectedBadge: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  expiredBadge: {
    backgroundColor: '#e5e7eb',
    color: '#4b5563',
  },
  completedBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  reviewDialog: {
    minWidth: '600px',
  },
  dialogSection: {
    marginBottom: '20px',
  },
  dialogSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '8px',
  },
  infoBox: {
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '6px',
    border: '1px solid #e1dfdd',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    ':last-child': {
      marginBottom: 0,
    },
  },
  infoLabel: {
    fontSize: '13px',
    color: '#605e5c',
    fontWeight: '500',
  },
  infoValue: {
    fontSize: '13px',
    color: '#323130',
    fontWeight: '600',
  },
  changeHighlight: {
    padding: '12px',
    backgroundColor: '#fff4e5',
    borderRadius: '6px',
    border: '1px solid #ffb366',
    marginTop: '12px',
  },
  valueComparison: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: '16px',
    alignItems: 'center',
  },
  valueBox: {
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #e1dfdd',
  },
  oldValue: {
    backgroundColor: '#fee2e2',
  },
  newValue: {
    backgroundColor: '#d1fae5',
  },
  arrow: {
    fontSize: '20px',
    color: '#008272',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 24px',
    color: '#605e5c',
  },
  emptyIcon: {
    fontSize: '72px',
    color: '#c8c6c4',
    marginBottom: '16px',
  },
  statsBar: {
    display: 'flex',
    gap: '24px',
    padding: '16px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#323130',
  },
  statLabel: {
    fontSize: '13px',
    color: '#605e5c',
    marginTop: '4px',
  },
});

interface EditRequest {
  id: string;
  field_name: string;
  current_value: string;
  requested_value: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'completed';
  requested_at: string;
  requested_by: string;
  reviewed_at?: string;
  reviewed_by?: string;
  reviewer_comments?: string;
  expires_at?: string;
}

interface RequestStats {
  pending: number;
  approved: number;
  rejected: number;
  expired: number;
}

const FIELD_LABELS: Record<string, string> = {
  legal_name: 'Legal Name',
  tin: 'Tax Identification Number',
  tax_registration_number: 'Tax Registration Number',
  registered_address: 'Registered Address',
  country: 'Country',
  primary_email: 'Primary Email',
};

const STATUS_COLORS: Record<string, any> = {
  pending: { bg: '#fef3cd', text: '#92400e' },
  approved: { bg: '#d1fae5', text: '#065f46' },
  rejected: { bg: '#fee2e2', text: '#991b1b' },
  expired: { bg: '#e5e7eb', text: '#4b5563' },
  completed: { bg: '#dbeafe', text: '#1e40af' },
};

export const CompanySetupEditRequests: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<EditRequest[]>([]);
  const [stats, setStats] = useState<RequestStats>({
    pending: 0,
    approved: 0,
    rejected: 0,
    expired: 0,
  });
  const [activeTab, setActiveTab] = useState<string>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<EditRequest | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewComments, setReviewComments] = useState('');
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    loadRequests();
    loadStats();
  }, [activeTab]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'all' ? '' : activeTab;
      const response = await ApiClient.get(`/api/v1/company-setup/edit-requests?status=${status}`);
      setRequests(response.data.requests || []);
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await ApiClient.get('/api/v1/company-setup/edit-requests/stats');
      setStats(response.data.stats || { pending: 0, approved: 0, rejected: 0, expired: 0 });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const openReviewDialog = (request: EditRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setReviewAction(action);
    setReviewComments('');
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedRequest) {
      return;
    }

    setReviewing(true);
    try {
      await ApiClient.post(`/api/v1/company-setup/edit-requests/${selectedRequest.id}/review`, {
        action: reviewAction,
        comments: reviewComments,
      });

      newToast(
        'success',
        `Request ${reviewAction === 'approve' ? 'approved' : 'rejected'} successfully`
      );

      setReviewDialogOpen(false);
      loadRequests();
      loadStats();
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setReviewing(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    if (!searchQuery) {
      return true;
    }
    const query = searchQuery.toLowerCase();
    return (
      FIELD_LABELS[req.field_name]?.toLowerCase().includes(query) ||
      req.requested_by.toLowerCase().includes(query) ||
      req.reason.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status: string) => {
    const icons: Record<string, any> = {
      pending: <ClockRegular />,
      approved: <CheckmarkCircleRegular />,
      rejected: <DismissCircleRegular />,
      expired: <HistoryRegular />,
      completed: <CheckmarkCircleRegular />,
    };

    return (
      <div
        className={styles.statusBadge}
        style={{
          backgroundColor: STATUS_COLORS[status]?.bg,
          color: STATUS_COLORS[status]?.text,
        }}
      >
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </div>
    );
  };

  const columns = [
    createTableColumn<EditRequest>({
      columnId: 'field_name',
      renderHeaderCell: () => 'Field',
      renderCell: item => (
        <TableCellLayout>
          <Text style={{ fontWeight: '600' }}>
            {FIELD_LABELS[item.field_name] || item.field_name}
          </Text>
        </TableCellLayout>
      ),
    }),
    createTableColumn<EditRequest>({
      columnId: 'requested_by',
      renderHeaderCell: () => 'Requested By',
      renderCell: item => (
        <TableCellLayout
          media={
            <Avatar
              size={28}
              color="brand"
              initials={item.requested_by.substring(0, 2).toUpperCase()}
            />
          }
        >
          {item.requested_by}
        </TableCellLayout>
      ),
    }),
    createTableColumn<EditRequest>({
      columnId: 'requested_at',
      renderHeaderCell: () => 'Date',
      renderCell: item => (
        <TableCellLayout>
          <Text>{new Date(item.requested_at).toLocaleDateString()}</Text>
          <Text style={{ fontSize: '12px', color: '#605e5c' }}>
            {new Date(item.requested_at).toLocaleTimeString()}
          </Text>
        </TableCellLayout>
      ),
    }),
    createTableColumn<EditRequest>({
      columnId: 'status',
      renderHeaderCell: () => 'Status',
      renderCell: item => <TableCellLayout>{getStatusBadge(item.status)}</TableCellLayout>,
    }),
    createTableColumn<EditRequest>({
      columnId: 'actions',
      renderHeaderCell: () => 'Actions',
      renderCell: item => (
        <TableCellLayout>
          {item.status === 'pending' && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                size="small"
                className={styles.approveButton}
                icon={<CheckmarkCircleRegular />}
                onClick={() => openReviewDialog(item, 'approve')}
              >
                Approve
              </Button>
              <Button
                size="small"
                className={styles.rejectButton}
                icon={<DismissCircleRegular />}
                onClick={() => openReviewDialog(item, 'reject')}
              >
                Reject
              </Button>
            </div>
          )}
          {item.status !== 'pending' && (
            <Button
              size="small"
              appearance="subtle"
              onClick={() => {
                setSelectedRequest(item);
                setReviewDialogOpen(true);
              }}
            >
              View Details
            </Button>
          )}
        </TableCellLayout>
      ),
    }),
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            className={styles.backButton}
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            onClick={() => navigate('/account/company-setup')}
          />
          <div>
            <Text className={styles.pageTitle}>Edit Requests</Text>
            <Text className={styles.pageSubtitle}>
              Review and manage locked field edit requests
            </Text>
          </div>
        </div>
      </div>

      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <Text className={styles.statValue}>{stats.pending}</Text>
          <Text className={styles.statLabel}>Pending</Text>
        </div>
        <div className={styles.statItem}>
          <Text className={styles.statValue}>{stats.approved}</Text>
          <Text className={styles.statLabel}>Approved</Text>
        </div>
        <div className={styles.statItem}>
          <Text className={styles.statValue}>{stats.rejected}</Text>
          <Text className={styles.statLabel}>Rejected</Text>
        </div>
        <div className={styles.statItem}>
          <Text className={styles.statValue}>{stats.expired}</Text>
          <Text className={styles.statLabel}>Expired</Text>
        </div>
      </div>

      <ContentCard>
        <div className={styles.tabContainer}>
          <TabList
            selectedValue={activeTab}
            onTabSelect={(_, data) => setActiveTab(data.value as string)}
          >
            <Tab value="pending">Pending ({stats.pending})</Tab>
            <Tab value="approved">Approved</Tab>
            <Tab value="rejected">Rejected</Tab>
            <Tab value="expired">Expired</Tab>
            <Tab value="all">All Requests</Tab>
          </TabList>
        </div>

        <div className={styles.filterBar}>
          <Field className={styles.searchBox}>
            <Input
              contentBefore={<SearchRegular />}
              placeholder="Search by field name, user, or reason..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </Field>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Spinner size="large" label="Loading requests..." />
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className={styles.emptyState}>
            <InfoRegular className={styles.emptyIcon} />
            <Text style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              No Edit Requests Found
            </Text>
            <Text>
              {searchQuery
                ? 'Try adjusting your search criteria'
                : `No ${activeTab === 'all' ? '' : activeTab} requests at this time`}
            </Text>
          </div>
        ) : (
          <DataGrid items={filteredRequests} columns={columns} sortable getRowId={item => item.id}>
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<EditRequest>>
              {({ item, rowId }) => (
                <DataGridRow<EditRequest> key={rowId}>
                  {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        )}
      </ContentCard>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={(_, data) => setReviewDialogOpen(data.open)}>
        <DialogSurface className={styles.reviewDialog}>
          <DialogBody>
            <DialogTitle>
              {selectedRequest?.status === 'pending'
                ? `${reviewAction === 'approve' ? 'Approve' : 'Reject'} Edit Request`
                : 'Request Details'}
            </DialogTitle>
            <DialogContent>
              {selectedRequest && (
                <>
                  <div className={styles.dialogSection}>
                    <Text className={styles.dialogSectionTitle}>Request Information</Text>
                    <div className={styles.infoBox}>
                      <div className={styles.infoRow}>
                        <Text className={styles.infoLabel}>Field:</Text>
                        <Text className={styles.infoValue}>
                          {FIELD_LABELS[selectedRequest.field_name] || selectedRequest.field_name}
                        </Text>
                      </div>
                      <div className={styles.infoRow}>
                        <Text className={styles.infoLabel}>Requested by:</Text>
                        <Text className={styles.infoValue}>{selectedRequest.requested_by}</Text>
                      </div>
                      <div className={styles.infoRow}>
                        <Text className={styles.infoLabel}>Requested on:</Text>
                        <Text className={styles.infoValue}>
                          {new Date(selectedRequest.requested_at).toLocaleString()}
                        </Text>
                      </div>
                      <div className={styles.infoRow}>
                        <Text className={styles.infoLabel}>Status:</Text>
                        {getStatusBadge(selectedRequest.status)}
                      </div>
                    </div>
                  </div>

                  <div className={styles.dialogSection}>
                    <Text className={styles.dialogSectionTitle}>Requested Change</Text>
                    <div className={styles.changeHighlight}>
                      <div className={styles.valueComparison}>
                        <div className={`${styles.valueBox} ${styles.oldValue}`}>
                          <Text style={{ fontSize: '12px', color: '#991b1b', fontWeight: '600' }}>
                            Current Value
                          </Text>
                          <Text style={{ fontSize: '14px', marginTop: '8px' }}>
                            {selectedRequest.current_value}
                          </Text>
                        </div>
                        <Text className={styles.arrow}>→</Text>
                        <div className={`${styles.valueBox} ${styles.newValue}`}>
                          <Text style={{ fontSize: '12px', color: '#065f46', fontWeight: '600' }}>
                            Requested Value
                          </Text>
                          <Text style={{ fontSize: '14px', marginTop: '8px' }}>
                            {selectedRequest.requested_value}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.dialogSection}>
                    <Text className={styles.dialogSectionTitle}>Reason for Change</Text>
                    <div className={styles.infoBox}>
                      <Text style={{ fontSize: '14px', color: '#323130' }}>
                        {selectedRequest.reason}
                      </Text>
                    </div>
                  </div>

                  {selectedRequest.status !== 'pending' && selectedRequest.reviewed_at && (
                    <div className={styles.dialogSection}>
                      <Text className={styles.dialogSectionTitle}>Review Details</Text>
                      <div className={styles.infoBox}>
                        <div className={styles.infoRow}>
                          <Text className={styles.infoLabel}>Reviewed by:</Text>
                          <Text className={styles.infoValue}>{selectedRequest.reviewed_by}</Text>
                        </div>
                        <div className={styles.infoRow}>
                          <Text className={styles.infoLabel}>Reviewed on:</Text>
                          <Text className={styles.infoValue}>
                            {new Date(selectedRequest.reviewed_at).toLocaleString()}
                          </Text>
                        </div>
                        {selectedRequest.reviewer_comments && (
                          <div style={{ marginTop: '12px' }}>
                            <Text className={styles.infoLabel}>Comments:</Text>
                            <Text style={{ fontSize: '14px', color: '#323130', marginTop: '4px' }}>
                              {selectedRequest.reviewer_comments}
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedRequest.status === 'pending' && (
                    <div className={styles.dialogSection}>
                      <Field
                        label="Review Comments"
                        hint="Optional: Add comments about this decision"
                      >
                        <Textarea
                          value={reviewComments}
                          onChange={e => setReviewComments(e.target.value)}
                          placeholder="Enter your comments here..."
                          rows={4}
                        />
                      </Field>
                    </div>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReviewDialogOpen(false)}>
                {selectedRequest?.status === 'pending' ? 'Cancel' : 'Close'}
              </Button>
              {selectedRequest?.status === 'pending' && (
                <Button
                  appearance="primary"
                  onClick={handleReviewSubmit}
                  disabled={reviewing}
                  style={{
                    backgroundColor: reviewAction === 'approve' ? '#107c10' : '#d13438',
                    color: '#ffffff',
                  }}
                >
                  {reviewing ? (
                    <Spinner size="tiny" />
                  ) : reviewAction === 'approve' ? (
                    'Approve Request'
                  ) : (
                    'Reject Request'
                  )}
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default CompanySetupEditRequests;
